import type { ActionFlowEngine } from "./action-flow-engine"
import type { StateManager } from "./state-manager"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WalletConfig {
  /** Base URL for the wallet API (e.g. "https://wallet.cogo.xyz/functions/v1") */
  baseUrl: string
  /** Optional API key sent as x-api-key header */
  apiKey?: string
  /** Optional JWT sent as Bearer token */
  jwt?: string
}

export interface WalletErrorResult {
  error: string
  code: string
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

/**
 * Register 8 Tier-1 wallet actions on an ActionFlowEngine instance.
 *
 * Actions registered:
 *   wallet.create        - POST /wallets
 *   wallet.list          - GET  /wallets
 *   wallet.getBalance    - GET  /wallets/:id/balance
 *   wallet.syncBalance   - POST /wallets/:id/sync
 *   wallet.send          - POST /wallets/:id/transactions
 *   wallet.getTxStatus   - GET  /transactions/:requestId
 *   wallet.getTxHistory  - GET  /wallets/:id/transactions
 *   wallet.estimateFee   - POST /wallets/estimate-fee
 *
 * Each action reads `params.resultTarget` (string) to determine where to
 * store the response in state. If omitted, a sensible default is used.
 *
 * @param engine  - The ActionFlowEngine to register actions on
 * @param state   - The StateManager used to store action results
 * @param config  - Wallet API configuration (baseUrl, apiKey, jwt)
 */
export function registerWalletActions(
  engine: ActionFlowEngine,
  state: StateManager,
  config: WalletConfig,
): void {
  // -- helpers --------------------------------------------------------------

  const buildHeaders = (): Record<string, string> => {
    const h: Record<string, string> = { "Content-Type": "application/json" }
    if (config.apiKey) h["x-api-key"] = config.apiKey
    if (config.jwt) h["Authorization"] = `Bearer ${config.jwt}`
    return h
  }

  const call = async (
    endpoint: string,
    method: string,
    body?: unknown,
  ): Promise<unknown> => {
    const opts: RequestInit = { method, headers: buildHeaders() }
    if (body && method !== "GET") {
      opts.body = JSON.stringify(body)
    }
    try {
      const res = await fetch(`${config.baseUrl}${endpoint}`, opts)
      const ct = res.headers.get("content-type") ?? ""
      const data = ct.includes("application/json")
        ? await res.json()
        : await res.text()
      if (!res.ok) {
        // Wrap non-JSON error text into a structured object
        return typeof data === "object" && data !== null
          ? data
          : { error: String(data), code: `HTTP_${res.status}` }
      }
      return data
    } catch (err) {
      return { error: String(err), code: "FETCH_ERROR" } satisfies WalletErrorResult
    }
  }

  /** Store a result in state, stripping a leading `#` if present. */
  const store = (target: string, data: unknown): void => {
    state.setState(target.replace(/^#/, ""), data)
  }

  // -- T5: wallet.create ----------------------------------------------------

  engine.registerAction("wallet.create", async (params) => {
    const resultTarget = String(params.resultTarget ?? "_results.wallet.create")
    // Everything except resultTarget is forwarded as body
    const { resultTarget: _rt, ...body } = params
    const data = await call("/wallets", "POST", body)
    store(resultTarget, data)
  })

  // -- T6: wallet.list ------------------------------------------------------

  engine.registerAction("wallet.list", async (params) => {
    const resultTarget = String(params.resultTarget ?? "_results.wallet.list")
    const network = params.network as string | undefined
    const qs = network ? `?network_id=${encodeURIComponent(network)}` : ""
    const data = await call(`/wallets${qs}`, "GET")
    store(resultTarget, data)
  })

  // -- T6: wallet.getBalance ------------------------------------------------

  engine.registerAction("wallet.getBalance", async (params) => {
    const resultTarget = String(params.resultTarget ?? "_results.wallet.balance")
    const walletId = String(params.walletId ?? "")
    const data = await call(`/wallets/${encodeURIComponent(walletId)}/balance`, "GET")
    store(resultTarget, data)
  })

  // -- T6: wallet.syncBalance -----------------------------------------------

  engine.registerAction("wallet.syncBalance", async (params) => {
    const resultTarget = String(params.resultTarget ?? "_results.wallet.syncBalance")
    const walletId = String(params.walletId ?? "")
    const data = await call(`/wallets/${encodeURIComponent(walletId)}/sync`, "POST")
    store(resultTarget, data)
  })

  // -- T7: wallet.send ------------------------------------------------------

  engine.registerAction("wallet.send", async (params) => {
    const resultTarget = String(params.resultTarget ?? "_results.wallet.send")
    const walletId = String(params.walletId ?? "")
    // Strip meta-params before forwarding as body
    const { walletId: _wid, resultTarget: _rt, ...body } = params
    const data = await call(
      `/wallets/${encodeURIComponent(walletId)}/transactions`,
      "POST",
      body,
    )
    store(resultTarget, data)
  })

  // -- T7: wallet.getTxStatus -----------------------------------------------

  engine.registerAction("wallet.getTxStatus", async (params) => {
    const resultTarget = String(params.resultTarget ?? "_results.wallet.txStatus")
    const requestId = String(params.requestId ?? "")
    const data = await call(`/transactions/${encodeURIComponent(requestId)}`, "GET")
    store(resultTarget, data)
  })

  // -- T7: wallet.getTxHistory ----------------------------------------------

  engine.registerAction("wallet.getTxHistory", async (params) => {
    const resultTarget = String(params.resultTarget ?? "_results.wallet.txHistory")
    const walletId = String(params.walletId ?? "")
    const qs = new URLSearchParams()
    if (params.network) qs.set("network", String(params.network))
    if (params.limit) qs.set("limit", String(params.limit))
    if (params.offset) qs.set("offset", String(params.offset))
    const qsStr = qs.toString() ? `?${qs.toString()}` : ""
    const data = await call(
      `/wallets/${encodeURIComponent(walletId)}/transactions${qsStr}`,
      "GET",
    )
    store(resultTarget, data)
  })

  // -- T7: wallet.estimateFee -----------------------------------------------

  engine.registerAction("wallet.estimateFee", async (params) => {
    const resultTarget = String(params.resultTarget ?? "_results.wallet.fee")
    const { resultTarget: _rt, ...body } = params
    const data = await call("/wallets/estimate-fee", "POST", body)
    store(resultTarget, data)
  })
}
